(function() {
    'use strict';
    /*
     * Copyright (c) 2014-15 In Hand Health.
     * All rights reserved.
     */

    angular
        .module('ihh-common-web')
        .service('IhhArrays', IhhArrays);

    IhhArrays.$inject = ['_'];

    /**
     * @ngdoc service
     * @memberof ihh-common-web
     * @name IhhArrays
     * @description This service contains various methods for operating on arrays.
     * Do not use <code>new</code> to create an instance. See the example below for how to use the service in a controller.
     * @example
     * // Declaring the utilities service in a controller.
     * myModule.controller('MyCtrl', ['IhhArrays', ..., function(IhhArrays, ...) {
     *    var someArray = ...
     *    var id = ...
     *
     *    $scope.someElement = Utilities.findById(someArrray, id);
     * }
     */
    function IhhArrays(_) {
        /**
         * Returns whether the passed argument is an array.
         * @ngdoc method
         * @name isArray
         * @memberOf IhhArrays
         * @param arg the argument to check
         * @returns {boolean} true if the argument is an array; false otherwise
         */
        this.isArray = function(arg) {
            return angular.isArray(arg);
        };

        /**
         * Adds the passed item to the array, if it is not already in the array. If the item is an object with an **id** property, then
         * equality is determined by matching {@link ihh-common-web.IhhArrays#indexOfById}. Otherwise, the item is checked for equality using **===**.
         * @name addWithNoDup
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} destArray the array to add an element to
         * @param {Object} item the item to put in the array if it is not already there
         */
        this.addWithNoDup = function(destArray, item) {
            var prevCopy;

            if (angular.isDefined(item)) {
                if (angular.isDefined(item.id)) {
                    if (this.indexOfById(destArray, item.id) === -1) {
                        destArray.push(item);
                    }
                    else if (item.edited) {
                        prevCopy = this.findById(destArray, item.id);
                        if (item.edited > prevCopy.edited) {
                            this.removeById(destArray, prevCopy.id);
                            destArray.push(item);
                        }
                    }
                }
                else if (destArray.indexOf(item) === -1) {
                    destArray.push(item);
                }
            }
        };
        /**
         * Adds all the passed item to the array, if it is not already in the array. Respects objects with **id** properties like
         * {@link ihh-common-web.IhhArrays#addWithNoDup}.
         * @name addAllWithNoDup
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} destArray the array to add an element to
         * @param {Array} srcArray the array of items to put in the array if it is not already there
         */
        this.addAllWithNoDup = function(destArray, srcArray) {
            if (angular.isArray(srcArray)) {
                for (var i = 0; i < srcArray.length; i++) {
                    this.addWithNoDup(destArray, srcArray[i]);
                }
            }
        };

        /**
         * Searches the passed array of objects for an item that matches the passed ID. The passed array
         * is assumed to be of objects that each have an **id** field.
         * @name findById
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} srcArray the array to search
         * @param {string} id the ID to search for
         * @return {object} the found item; or null if none was found
         */
        this.findById = function(srcArray, id) {
            var index = this.indexOfById(srcArray, id);

            return index >= 0 ? srcArray[index] : null;
        };

        /**
         * Searches the passed array of objects for an item that matches the passed ID, and removes it from the array. Does
         * nothing if a matching item is not found.
         * @name removeById
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {array} srcArray the array to remove from
         * @param {string} id the ID of the item to remove
         */
        this.removeById = function(srcArray, id) {
            var index = this.indexOfById(srcArray, id);

            if (index >= 0) {
                this.deleteElement(srcArray, index);
            }
        };

        /**
         * Searches the passed array of objects for an item that matches the passed ID. The passed array
         * is assumed to be of objects that each have an **id** field.
         * @name indexOfById
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} srcArray the array to search
         * @param {string} id the ID to search for
         * @return {Integer} the index of the found item, or -1 if it was not found
         */
        this.indexOfById = function(srcArray, id) {
            return this.findIndex(srcArray, 'id', id);
        };

        /**
         * Searches the passed array of objects for an item that matches the passed TEMP ID. The passed array
         * is assumed to be of objects that each have an **_id** field.
         * @name indexOfByTempId
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} srcArray the array to search
         * @param {string} id the TEMP ID to search for
         * @return {Integer} the index of the found item, or -1 if it was not found
         */
        this.indexOfByTempId = function(srcArray, id) {
            return this.findIndex(srcArray, '_id', id);
        };

        /**
         * Searches the passed array of objects for an item that matches the passed ID.
         * @name findIndex
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} srcArray the array to search
         * @param {string} id the ID to search for
         * @return {Integer} the index of the found item, or -1 if it was not found
         */
        this.findIndex = function(srcArray, key, value) {
            return _.findIndex(srcArray, key, value);
        };

        /**
         * Updates the passed array by moving the element with the indicated index a number of array slots
         * indicated by **delta**.
         * @name moveElement
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {array} array the array to mutate
         * @param {number} index the index of the element to move
         * @param {number} delta the number of slots to move the element (negative is up)
         */
        this.moveElement = function(array, index, delta) {
            var newIndex = index + delta;
            var elem;

            if (angular.isArray(array) && index >= 0 && index < array.length && newIndex >= 0 && newIndex < array.length && delta !== 0) {
                elem = array.splice(index, 1)[0];
                array.splice(newIndex, 0, elem);
            }
        };

        /**
         * Deletes the array element at the given index, if it exists.
         * @name deleteElement
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} array the array to delete from
         * @param {Number} index the index of the element to delete
         */
        this.deleteElement = function(array, index) {
            if (angular.isArray(array) && index >= 0 && index < array.length) {
                array.splice(index, 1);
            }
        };

        /**
         * Deletes the first element from the array that matches the passed value (as determined by **indexOf**).
         * @name deleteElementByValue
         * @ngdoc method
         * @memberOf IhhArrays
         * @param {Array} array the array to remove from
         * @param {Object} value the value to remove
         */
        this.deleteElementByValue = function(array, value) {
            var index;

            if (angular.isArray(array)) {
                index = array.indexOf(value);

                if (index >= 0) {
                    array.splice(index, 1);
                }
            }
        };

        /**
         * Finds the index of a string in an array.
         * @name findTag
         * @ngdoc method
         * @memberOf ihh-utilities.Utilities
         * @param {array} array the array to check against
         * @param {object} tag the tag to check for
         * @returns {number} the index of the item
         */
        this.findTag = function(array, tag) {
            var isString = angular.isString(tag);
            var lowerCaseTag;

            if (!angular.isArray(array)) {
                return -1;
            }
            else {
                if (isString) {
                    lowerCaseTag = tag.toLowerCase();
                }

                return _.findIndex(array, function(elem) {
                    if (isString) {
                        return lowerCaseTag === elem.toLowerCase();
                    }
                    else {
                        return elem.id === tag.id;
                    }
                });
            }
        };

        /**
         * Removes the item matching the string in an array.
         * @name removeTag
         * @ngdoc method
         * @memberOf ihh-utilities.Utilities
         * @param {array} array the array to check against
         * @param {object} tag the tag to check for
         */
        this.removeTag = function(array, tag) {
            var i = this.findTag(array, tag);

            this.deleteElement(array, i);
        };

        /**
         * Adds the string in the array if it's not present and of min length.
         * @name addTag
         * @ngdoc method
         * @memberOf ihh-utilities.Utilities
         * @param {array} array the array to check against
         * @param {object} tag the tag to check for
         * @param {number} minLength the minimum length of characters the tag must have
         */
        this.addTag = function(array, tag, minLength) {
            if (!angular.isString(tag) && angular.isArray(tag)) {
                tag = tag[0];
            }

            var tagExists = this.findTag(array, tag) > -1;
            var isOfMinLength = tag ? tag.length > minLength : false;
            var canBeAdded = !tagExists && (isOfMinLength || angular.isObject(tag));

            if (canBeAdded) {
                array.push(tag);
            }

            return canBeAdded;
        };

    }
})();